import React, { useState, useEffect, useMemo } from "react";
import eventBus from "../../components/eventBus";
import Logger from "../../utils/Logger";
import httpClient from "../../services/httpClient.js";
import ReservationTable from "../../components/ReservationTable";
import { useAuth } from "../../AuthContext";
import { useTranslation } from "react-i18next";

const Cars = () => {
  const { t } = useTranslation();
  const [date, setDate] = useState(new Date());
  const [rawReservations, setRawReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allCars, setAllCars] = useState([]);
  const [onlyMine, setOnlyMine] = useState(false);
  const { userData } = useAuth();

  // id → colorHex lookup built from the /cars endpoint
  const carColorMap = useMemo(() => {
    const map = new Map();
    allCars.forEach((car) => map.set(car.id, car.colorHex));
    return map;
  }, [allCars]);

  const fetchMonthAndYearData = async (newDate, mine = onlyMine) => {
    setRawReservations([]);

    const newMonth = newDate.getMonth() + 1; // getMonth() returns 0-11
    const newYear = newDate.getFullYear();

    setDate(newDate);

    Logger.log(
      `Fetching data for month: ${newMonth}, year: ${newYear}, mine: ${mine}`,
    );
    // setLoading(true);
    setError(null);

    try {
      let url;
      if (mine) {
        url = `/CarReservations/month/mine?year=${newYear}&month=${newMonth}`;
      } else {
        url = `/CarReservations/month?year=${newYear}&month=${newMonth}`;
      }

      const response = await httpClient.get(url, { withAuth: true });

      setRawReservations(response.data);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        t("reservations.common.fetchDataError");
      setError(errorMessage);
      Logger.error("Błąd podczas pobierania danych:", error);
      Logger.error("Error details:", {
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url,
      });
    } finally {
      setLoading(false);
    }
  };

  // Funkcja do pobierania wszystkich samochodów dla legendy
  const fetchAllCars = async () => {
    try {
      Logger.log("Pobieranie wszystkich samochodów z /delegation/cars");
      const response = await httpClient.get("/cars", {
        withAuth: true,
      });
      const cars = response.data;
      Logger.log("Sukces - pobrano samochodów:", cars.length);
      setAllCars(cars);
    } catch (error) {
      Logger.log("Błąd pobierania wszystkich samochodów:", error);
      setAllCars([]);
    }
  };

  // Re-derived whenever raw reservations OR the car color map changes
  const transformedReservations = useMemo(() => {
    return rawReservations.map((reservation) => ({
      title: `${reservation.user.firstName} ${reservation.user.lastName} - ${reservation.reason}`,
      start: new Date(reservation.startDateLocal),
      end: new Date(reservation.endDateLocal),
      allDay: false,
      resource: {
        ...reservation,
        color:
          carColorMap.get(reservation.car?.id) ||
          reservation.car?.colorHex ||
          "#999999",
        type: "car",
        userName: `${reservation.user?.firstName} ${reservation.user?.lastName}`,
      },
    }));
  }, [rawReservations, carColorMap]);

  useEffect(() => {
    fetchMonthAndYearData(new Date());

    // Pobierz wszystkie samochody dla legendy - z error handlingiem
    fetchAllCars().catch((error) => {
      Logger.error("Błąd w useEffect podczas pobierania samochodów:", error);
      // Nie przerywamy działania komponentu, tylko logujemy błąd
    });
  }, []);

  useEffect(() => {
    Logger.log("onlyMine state changed:", onlyMine);
    fetchMonthAndYearData(date, onlyMine);
  }, [onlyMine]);

  // Subscribe to car-reservation events
  useEffect(() => {
    const handleCarReservationDeleted = () => {
      Logger.log("Car reservation deleted event received, refreshing data...");
      // Small delay to ensure backend has processed the deletion
      setTimeout(() => {
        fetchMonthAndYearData(date);
      }, 100);
    };

    const handleCarReservationUpdated = () => {
      Logger.log("Car reservation updated event received, refreshing data...");
      // Small delay to ensure backend has processed the update
      setTimeout(() => {
        fetchMonthAndYearData(date);
      }, 100);
    };

    const handleReFetchCarReservations = () => {
      Logger.log(
        "Re-fetch car reservations event received, refreshing data...",
      );
      // Small delay to ensure backend has processed the changes
      setTimeout(() => {
        fetchMonthAndYearData(date);
      }, 100);
    };

    eventBus.on("car-reservation-deleted", handleCarReservationDeleted);
    eventBus.on("car-reservation-updated", handleCarReservationUpdated);
    eventBus.on("reFetchCarReservations", handleReFetchCarReservations);

    // Cleanup subscription on component unmount
    return () => {
      eventBus.off("car-reservation-deleted", handleCarReservationDeleted);
      eventBus.off("car-reservation-updated", handleCarReservationUpdated);
      eventBus.off("reFetchCarReservations", handleReFetchCarReservations);
    };
  }, [date]); // Add date dependency

  // Filtrowanie danych
  const filteredData = useMemo(() => {
    let data = transformedReservations;

    // Filtracja tylko moich rezerwacji
    if (onlyMine && userData?.userName) {
      data = data.filter((item) => {
        return item.resource?.userName === userData.userName;
      });
    }

    return data;
  }, [transformedReservations, onlyMine, userData]);

  // Legend: always driven from allCars (authoritative source with colorHex)
  const uniqueCars = useMemo(() => {
    return allCars
      .filter((car) => car)
      .map((car) => ({
        key: car.id,
        label: `${car.make} ${car.model} ${car.licensePlate}`,
        color: car.colorHex || "#999999",
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "pl"));
  }, [allCars]);

  // Przycisk rezerwacji
  const button = {
    createLink: "/rezerwacje/samochody/zarezerwuj",
    createLabel: t("reservations.cars.makeReservation"),
  };

  // Przycisk zarządzania dla admina
  const adminButton =
    userData.userRole === "admin"
      ? {
          link: "/administrator/samochody",
          label: t("reservations.cars.manageCars"),
        }
      : null;

  return (
    <>
      <ReservationTable
        title={t("reservations.cars.listTitle")}
        onMonthAndYearChange={fetchMonthAndYearData}
        data={filteredData}
        loading={loading}
        error={error}
        button={button}
        adminButton={adminButton}
        legendItems={uniqueCars}
        legendTitle={t("reservations.cars.legendTitle")}
        showOnlyMine={onlyMine}
        onToggleOnlyMine={() => setOnlyMine(!onlyMine)}
        currentDate={date}
      />
    </>
  );
};

export default Cars;
